/**
 * Afterburning turbofan.
 *
 * ## Thrust is commanded, not balanced
 *
 * The piston model's central idea is that RPM is a *state*: the shaft accelerates because engine
 * torque exceeds propeller torque, and every characteristic of a fixed-pitch aeroplane falls out
 * of that one equation. A turbofan is not like that. The engine control unit holds whatever core
 * speed the lever asks for and the fan does not care what the airframe is doing, so the spool is a
 * first-order lag chasing a commanded value rather than an equilibrium between two torques.
 *
 * That difference is the whole feel of the thing. A piston single answers the throttle in a
 * quarter of a second; a big military turbofan takes four or five seconds from idle to military
 * power, which is why a fighter pilot leads the throttle into a turn and why a late go-around is
 * a genuine problem. {@link JetEngineConfig.spoolTimeS} is that number, and it is asymmetric —
 * spooling down is slower than spooling up on a real engine, because the compressor has to be
 * driven rather than driving.
 *
 * ## What thrust actually depends on
 *
 *     T = T_mil · λ_ρ(σ) · λ_M(M) · (N₂ normalised) · (1 + AB·(ratio − 1))
 *
 *   - **Density lapse** `σ^n` with `n` around 0.8 for a low-bypass military fan. It is not
 *     linear in σ: the compressor's pressure ratio helps at altitude, so thrust falls off more
 *     slowly than density does.
 *   - **Mach lapse**, a table. This is the term that makes a fighter interesting. Static thrust
 *     falls as the aircraft accelerates through the transonic — the fan is swallowing air that is
 *     already moving — and then *recovers* above about M1.2 as ram compression at the intake
 *     starts adding pressure ratio for free. Without the recovery the aeroplane cannot go
 *     supersonic at altitude at all, and the M1.6 gate simply fails.
 *   - **Reheat**, which is a multiplier on the dry thrust and a much larger multiplier on fuel
 *     flow. Roughly +50 % thrust for +250 % fuel is the classic trade, and it is why the
 *     afterburner is a few-minute device.
 *
 * ## The lever
 *
 * One 0…1 throttle, as everywhere else in this simulator, because `ControlInputs` is a plain
 * serialisable struct that a network packet has to be able to carry (non-negotiable #7). The last
 * stretch of its travel is reheat, past {@link JetEngineConfig.afterburnerGate} — which is what
 * the physical detent on the real lever is. A config that sets the gate to 1 has a dry engine and
 * needs no other change anywhere.
 *
 * `updateFixed(dt)`: yes, at 120 Hz. Allocation-free after construction.
 */

import { Curve1D } from './aero/tables.js';
import { ISA_SEA_LEVEL_DENSITY, type AtmosphereSample } from './atmosphere.js';
import { clamp, moveTowards } from '../core/math.js';
import { makePropulsionOutput, type Powerplant, type PropulsionOutput } from './powerplant.js';

export interface JetEngineConfig {
  /** Maximum dry (military) thrust at sea level, static, ISA [N]. */
  militaryThrustN: number;
  /** Thrust with full reheat as a multiple of military. Around 1.5 for a modern fighter fan. */
  afterburnerThrustRatio: number;
  /**
   * Throttle position at which reheat begins, 0…1.
   *
   * The detent. Below it the lever spans idle to military; above it, military to full reheat.
   */
  afterburnerGate: number;
  /** Idle thrust as a fraction of military, at sea level static. */
  idleThrustFraction: number;
  /** Time to spool from idle to military [s]. */
  spoolTimeS: number;
  /**
   * How much slower spooling *down* is than spooling up, as a multiplier on {@link spoolTimeS}.
   * Above 1: a compressor being driven takes longer to give its energy back than to take it.
   */
  spoolDownFactor: number;
  /** How long full reheat takes to light and to go out [s]. Much faster than the spool. */
  afterburnerLightTimeS: number;
  /** Exponent `n` in the density lapse `σⁿ`. */
  densityLapseExponent: number;
  /** Flight Mach numbers for the thrust lapse table. */
  machNumbers: readonly number[];
  /** Thrust as a fraction of the static value, at those Mach numbers. */
  machThrustFactor: readonly number[];
  /** Core speed at idle, as a percentage. */
  idleN2Percent: number;
  /** Core speed at military power, as a percentage. */
  militaryN2Percent: number;
  /** Fuel flow at military power, sea level static [kg/s]. */
  militaryFuelFlowKgPerS: number;
  /** Fuel flow multiplier at full reheat. */
  afterburnerFuelFlowRatio: number;
  /** Time for the starter to bring the core to idle [s]. */
  startTimeS: number;
}

/**
 * Below this commanded fraction the engine is at idle.
 *
 * Not zero: a running turbine always turns, and a lever at the stop is flight idle rather than
 * shutdown. Cutting the engine is `ignitionOn = false`, exactly as it is on the piston.
 */
const MIN_COMMAND = 0;

/** Core speed below which a spooling-down turbine is considered stopped [% N2]. */
const STOPPED_N2 = 1;

export class Turbofan implements Powerplant {
  private readonly config: JetEngineConfig;
  private readonly machCurve: Curve1D;

  /** Core speed, as a percentage of maximum. The engine's one real state. */
  private n2Percent: number;
  /** Reheat, 0…1, lagged separately because it lights far faster than the core spools. */
  private afterburner = 0;
  private runningValue = true;

  ignitionOn = true;
  starterEngaged = false;

  readonly output = makePropulsionOutput();

  constructor(config: JetEngineConfig) {
    this.config = config;
    this.machCurve = new Curve1D(config.machNumbers, config.machThrustFactor, 'jet thrust vs Mach');
    this.n2Percent = config.idleN2Percent;
  }

  /**
   * Core speed as a percentage of maximum — N2, the number on the gauge.
   *
   * Named `rpm` because {@link Powerplant} calls it that and everything downstream wants "how fast
   * is the engine turning" without caring which units the type of engine happens to quote.
   */
  get rpm(): number {
    return this.n2Percent;
  }

  set rpm(value: number) {
    this.n2Percent = clamp(value, 0, 110);
  }

  get running(): boolean {
    return this.runningValue;
  }

  /** Reheat, 0…1. Read by the flame, the shimmer and the audio through the flight state. */
  get afterburnerFraction(): number {
    return this.afterburner;
  }

  shutDown(): void {
    this.runningValue = false;
    this.ignitionOn = false;
    this.starterEngaged = false;
  }

  engageStarter(): void {
    this.ignitionOn = true;
    this.starterEngaged = true;
  }

  releaseStarter(): void {
    this.starterEngaged = false;
  }

  forceRunning(rpm: number): void {
    this.runningValue = true;
    this.ignitionOn = true;
    this.starterEngaged = false;
    this.n2Percent = clamp(rpm, this.config.idleN2Percent, 110);
  }

  /**
   * Where the spool would settle at this lever position.
   *
   * A turbofan's steady state is *commanded*, so unlike the piston's version this is not a root
   * find — it is the same interpolation the transient chases, evaluated directly. The trim solver
   * calls it so that every one of its thousands of evaluations sees a settled engine rather than
   * one still spooling.
   */
  equilibriumRpm(throttle: number, _axialSpeedMps: number, _air: AtmosphereSample): number {
    return this.commandedN2(throttle);
  }

  /** Core speed the lever is asking for [% N2]. */
  private commandedN2(throttle: number): number {
    const { idleN2Percent, militaryN2Percent, afterburnerGate } = this.config;
    // Below the gate the lever spans idle to military. Above it the core is already at military —
    // reheat adds fuel in the jetpipe, it does not spin the core faster.
    const dry = clamp(throttle, MIN_COMMAND, 1) / Math.max(1e-6, afterburnerGate);
    return idleN2Percent + (militaryN2Percent - idleN2Percent) * clamp(dry, 0, 1);
  }

  /** How far into reheat the lever is, 0…1. */
  private commandedAfterburner(throttle: number): number {
    const gate = this.config.afterburnerGate;
    if (gate >= 1) return 0;
    return clamp((clamp(throttle, 0, 1) - gate) / (1 - gate), 0, 1);
  }

  /**
   * Dry thrust available right now, before reheat [N].
   *
   * Exposed for the gates, which need to check the thrust-to-weight claim and the Mach lapse
   * without integrating an aeroplane to get at them.
   */
  militaryThrustAt(air: AtmosphereSample, machNumber: number): number {
    const sigma = air.densityKgPerM3 / ISA_SEA_LEVEL_DENSITY;
    const densityFactor = Math.pow(Math.max(1e-6, sigma), this.config.densityLapseExponent);
    return this.config.militaryThrustN * densityFactor * this.machCurve.at(Math.max(0, machNumber));
  }

  updateFixed(
    throttle: number,
    axialSpeedMps: number,
    _alphaRad: number,
    _betaRad: number,
    air: AtmosphereSample,
    dtSeconds: number,
  ): PropulsionOutput {
    const config = this.config;
    const out = this.output;

    // --- the core -----------------------------------------------------------------------
    /*
     * Three states, mirroring the piston's, and for the same reason: a start is a thing you can
     * hear happening, and it has to exist in the model before it can exist in the sound.
     *
     *   - **Running.** The core chases the lever through a first-order lag.
     *   - **Starting.** The starter drives it toward idle; at idle it lights and is running.
     *   - **Shut down.** It spools down to a stop, slowly, which is why a jet keeps turning long
     *     after the fuel is cut.
     */
    let target: number;
    let timeConstant: number;
    if (this.runningValue) {
      target = this.commandedN2(throttle);
      // Down is slower than up: the compressor has to be driven rather than driving.
      const spooling = target >= this.n2Percent ? config.spoolTimeS : config.spoolTimeS * config.spoolDownFactor;
      timeConstant = spooling;
    } else if (this.starterEngaged && this.ignitionOn) {
      target = config.idleN2Percent;
      timeConstant = config.startTimeS;
    } else {
      target = 0;
      // A windmilling core takes a long time to stop; there is nothing braking it.
      timeConstant = config.spoolTimeS * config.spoolDownFactor * 3;
    }

    // First-order lag, integrated explicitly so the rate is a fraction of the *remaining* error
    // and the response is the exponential a real spool is, rather than a linear ramp.
    const span = Math.max(1, config.militaryN2Percent - config.idleN2Percent);
    const rate = span / Math.max(0.05, timeConstant);
    this.n2Percent = moveTowards(this.n2Percent, target, rate * dtSeconds);

    if (!this.runningValue && this.ignitionOn && this.n2Percent >= config.idleN2Percent * 0.95) {
      this.runningValue = true;
    }
    if (this.runningValue && !this.ignitionOn) this.runningValue = false;
    if (!this.runningValue && this.n2Percent < STOPPED_N2) this.n2Percent = 0;

    // --- reheat --------------------------------------------------------------------------
    // Only available with the core at (or very near) military: a real afterburner will not light
    // below about 90 % N2, which is why slamming the lever through the gate at idle gives you a
    // spool first and a light second.
    const coreReady =
      this.runningValue && this.n2Percent >= config.militaryN2Percent * 0.9 ? 1 : 0;
    const afterburnerTarget = this.commandedAfterburner(throttle) * coreReady;
    this.afterburner = moveTowards(
      this.afterburner,
      afterburnerTarget,
      dtSeconds / Math.max(0.05, config.afterburnerLightTimeS),
    );

    // --- thrust ---------------------------------------------------------------------------
    const machNumber = Math.max(0, axialSpeedMps) / Math.max(1, air.speedOfSoundMps);
    const military = this.militaryThrustAt(air, machNumber);

    // Normalise the core speed onto idle→military so the transient thrust follows the spool.
    const idleFraction = config.idleThrustFraction;
    const coreFraction = clamp(
      (this.n2Percent - config.idleN2Percent) / span,
      -config.idleN2Percent / span,
      1.2,
    );
    const dryFraction = this.runningValue
      ? idleFraction + (1 - idleFraction) * Math.max(0, coreFraction)
      : 0;

    const reheat = 1 + this.afterburner * (config.afterburnerThrustRatio - 1);
    const thrust = military * dryFraction * reheat;

    // --- fuel -------------------------------------------------------------------------------
    const fuelFlow =
      config.militaryFuelFlowKgPerS *
      dryFraction *
      (1 + this.afterburner * (config.afterburnerFuelFlowRatio - 1));

    out.thrustN = thrust;
    out.rpm = this.n2Percent;
    out.afterburnerFraction = this.afterburner;
    out.fuelFlowKgPerS = this.runningValue ? fuelFlow : 0;
    /*
     * Everything below is zero, and the zeros are the point.
     *
     * A jet has no propeller, so it has no torque reaction, no P-factor and no slipstream — the
     * four left-turning tendencies the trainer is built around simply do not exist on it, and the
     * fighter's complete lack of them is a large part of why it feels like a different machine.
     * `fdm.ts` adds all four unconditionally; it does not need to know that three of them are
     * zero here, which is exactly the property that keeps it free of aircraft-specific branches.
     */
    out.torqueNm = 0;
    out.rollMomentNm = 0;
    out.yawMomentNm = 0;
    out.pitchMomentNm = 0;
    out.slipstreamAxialMps = 0;
    out.slipstreamSwirlYMps = 0;
    out.powerW = 0;
    // Propulsive efficiency for a jet is thrust power over the chemical power in the fuel. Kept
    // as a ratio of useful work to thrust×speed so the field means something comparable.
    out.efficiency = thrust > 1 && axialSpeedMps > 1 ? Math.min(1, axialSpeedMps / 600) : 0;
    return out;
  }
}
